import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { createMarketplaceListing, startMarketplaceOAuth } from '../services/api';
import '../styles/marketplace.css';

export default function MarketplaceNew() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const marketplaceToken = searchParams.get('marketplace_token') || '';
    const displayName = searchParams.get('display_name') || '';

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [negotiable, setNegotiable] = useState(false);
    const [country, setCountry] = useState('');
    const [state, setState] = useState('');
    const [city, setCity] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [contactDiscord, setContactDiscord] = useState('');
    const [images, setImages] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    async function handleLogin() {
        try {
            const data = await startMarketplaceOAuth();
            window.location.href = data.oauth_url;
        } catch { setError('Erro ao iniciar verificação.'); }
    }

    function handleImages(e) {
        const files = Array.from(e.target.files).slice(0, 5);
        setImages(files);
        setPreviews(files.map(f => URL.createObjectURL(f)));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!title.trim() || !description.trim() || !country.trim() || !state.trim()) return;
        if (!contactEmail.trim() && !contactPhone.trim() && !contactDiscord.trim()) {
            setError(t('marketplace.contactRequired'));
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const form = new FormData();
            form.append('marketplace_token', marketplaceToken);
            form.append('title', title.trim());
            form.append('description', description.trim());
            form.append('price', negotiable ? '' : price);
            form.append('negotiable', negotiable ? 'true' : 'false');
            form.append('country', country.trim());
            form.append('state', state.trim());
            form.append('city', city.trim());
            form.append('contact_email', contactEmail.trim());
            form.append('contact_phone', contactPhone.trim());
            form.append('contact_discord', contactDiscord.trim());
            images.forEach(img => form.append('images', img));
            const result = await createMarketplaceListing(form);
            navigate('/marketplace');
        } catch (err) {
            const msg = err?.error || '';
            if (msg.includes('Token')) setError(t('marketplace.tokenExpired'));
            else setError(t('marketplace.submitError'));
        } finally {
            setSubmitting(false);
        }
    }

    if (!marketplaceToken) {
        return (
            <div className="App">
                <Header />
                <main className="main-content">
                    <div className="marketplace-new">
                        <h1>{t('marketplace.createBtn')}</h1>
                        <div className="marketplace-oauth-box">
                            <h2>{t('marketplace.loginRequired')}</h2>
                            <p>{t('marketplace.loginHint')}</p>
                            <button className="btn-iracing" onClick={handleLogin}>
                                {t('marketplace.loginBtn')}
                            </button>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="App">
            <Header />
            <main className="main-content">
                <div className="marketplace-new">
                    <h1>{t('marketplace.createBtn')}</h1>
                    <p className="organizer-subtitle">
                        ✓ {t('marketplace.verifiedSeller')}: <strong>{displayName}</strong>
                    </p>

                    {error && (
                        <div style={{ background: '#3d2020', color: '#e07070', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem' }}>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <label className="form-label">{t('marketplace.titleLabel')} *</label>
                        <input
                            className="form-input"
                            placeholder={t('marketplace.titlePlaceholder')}
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            required
                        />

                        <label className="form-label">{t('marketplace.descriptionLabel')} *</label>
                        <textarea
                            className="form-textarea"
                            placeholder={t('marketplace.descriptionPlaceholder')}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            required
                        />

                        <label className="form-label">{t('marketplace.priceLabel')}</label>
                        <input
                            className="form-input"
                            type="number"
                            placeholder={t('marketplace.pricePlaceholder')}
                            value={price}
                            onChange={e => setPrice(e.target.value)}
                            disabled={negotiable}
                        />
                        <div className="checkbox-row">
                            <input type="checkbox" id="negotiable" checked={negotiable} onChange={e => setNegotiable(e.target.checked)} />
                            <label htmlFor="negotiable" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>{t('marketplace.negotiableLabel')}</label>
                        </div>

                        <label className="form-label" style={{ marginTop: 8 }}>{t('marketplace.locationSectionLabel')}</label>
                        <div className="form-row">
                            <div>
                                <label className="form-label">{t('marketplace.countryLabel')} *</label>
                                <input className="form-input" placeholder={t('marketplace.countryPlaceholder')} value={country} onChange={e => setCountry(e.target.value)} required />
                            </div>
                            <div>
                                <label className="form-label">{t('marketplace.stateLabel')} *</label>
                                <input className="form-input" placeholder={t('marketplace.statePlaceholder')} value={state} onChange={e => setState(e.target.value)} required />
                            </div>
                        </div>
                        <label className="form-label">{t('marketplace.cityLabel')}</label>
                        <input className="form-input" placeholder={t('marketplace.cityPlaceholder')} value={city} onChange={e => setCity(e.target.value)} />

                        <label className="form-label" style={{ marginTop: 8 }}>{t('marketplace.contactSectionLabel')} *</label>
                        <p style={{ fontSize: '0.78rem', color: '#9ba3ab', margin: '0 0 10px' }}>{t('marketplace.contactHint')}</p>

                        <label className="form-label">{t('marketplace.contactEmailLabel')}</label>
                        <input className="form-input" type="email" placeholder="email@exemplo.com" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />

                        <label className="form-label">{t('marketplace.contactPhoneLabel')}</label>
                        <input className="form-input" type="tel" placeholder="(11) 99999-9999" value={contactPhone} onChange={e => setContactPhone(e.target.value)} />

                        <label className="form-label">{t('marketplace.contactDiscordLabel')}</label>
                        <input className="form-input" placeholder="usuario#1234 ou @usuario" value={contactDiscord} onChange={e => setContactDiscord(e.target.value)} />

                        <label className="form-label" style={{ marginTop: 8 }}>{t('marketplace.imagesLabel')}</label>
                        <p style={{ fontSize: '0.78rem', color: '#9ba3ab', margin: '0 0 8px' }}>{t('marketplace.imagesHint')}</p>
                        <input type="file" accept="image/*" multiple className="form-input" onChange={handleImages} />
                        {previews.length > 0 && (
                            <div className="image-preview">
                                {previews.map((src, i) => <img key={i} src={src} alt="" />)}
                            </div>
                        )}

                        <button className="btn-submit" type="submit" disabled={submitting}>
                            {submitting ? t('common.loading') : t('marketplace.submitBtn')}
                        </button>
                    </form>
                </div>
            </main>
            <Footer />
        </div>
    );
}
